import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';

// Public layout + pages
import { PublicLayout } from './components/public/PublicLayout';
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import ServicesPage from './pages/public/ServicesPage';
import DestinationsPage from './pages/public/DestinationsPage';
import PlanPage from './pages/public/PlanPage';
import BlogPage from './pages/public/BlogPage';
import BlogPostPage from './pages/public/BlogPostPage';
import GuidesPage from './pages/public/GuidesPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage';
import SetupAccountPage from './pages/public/SetupAccountPage';
import JacksonSuiteLanding from './pages/public/JacksonSuiteLanding';
import JacksonSuiteLogin from './pages/public/JacksonSuiteLogin';
import SuiteAccessLogin from './pages/public/SuiteAccessLogin';
import SuiteLandingPublic from './portal/pages/public/SuiteLandingPublic';
import OwnerAccessHub from './pages/OwnerAccessHub';
import OwnerAccessLogin from './pages/OwnerAccessLogin';
import NeutralAccessPage from './pages/public/NeutralAccessPage';

/**
 * Wave 12.w — RootRouter.
 *
 * The Emergent preview button always opens `/`, but the user lives
 * inside private portals 95% of the time. On preview/dev hosts the
 * root redirects straight to the Owner Hub (which is PIN-gated, so
 * still private). On production hosts `/` keeps showing the calm
 * public marketing page.
 */
function RootRouter() {
  const host = (typeof window !== "undefined" && window.location?.hostname) || "";
  const isPreview = /\.preview\.emergentagent\.com$/.test(host)
    || /\.emergent\.host$/.test(host)
    || host === "localhost"
    || host === "127.0.0.1";
  if (isPreview && !new URLSearchParams(window.location.search).has("public")) {
    return <Navigate to="/owner-access" replace />;
  }
  return <CMSLivePage slug="home" Fallback={HomePage} />;
}

/**
 * Feb 2026 — Short-form client slug router.
 *
 * Lets the advisor send simple links like:
 *   https://travel.verault.co/jackson
 *   https://travel.verault.co/ifearulundu
 *   https://travel.verault.co/[client-last-name]
 *
 * Any single-segment path that didn't match a specific public route is
 * treated as a client suite slug and forwarded to the protected door
 * `/suite/{slug}/access`. We exclude a small list of reserved
 * top-level paths to avoid hijacking known public pages.
 */
const SHORT_SLUG_RESERVED = new Set([
  "about", "services", "destinations", "plan", "contact",
  "blog", "guides", "trip", "preview",
  "login", "access", "forgot-password", "reset-password", "setup-account",
  "owner-access", "owner-hub", "internal-links",
  "advisor", "portal", "owner", "client", "clients", "admin",
  "manage", "settings", "communications-hub", "api", "suite",
  "intake", "form", "onboarding", "itinerary", "travels",
  "favicon.ico", "robots.txt", "sitemap.xml", "manifest.webmanifest", "sw.js",
]);

function ShortSlugRouter() {
  const { slug = "" } = useParams();
  const cleaned = (slug || "").trim().toLowerCase();
  if (!cleaned || SHORT_SLUG_RESERVED.has(cleaned)) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/suite/${cleaned}/access`} replace />;
}
import PreviewPage from './pages/PreviewPage';
import CMSLivePage from './pages/CMSLivePage';
import LandingPage from './portal/pages/public/LandingPage';
import ScrollToTop from './components/ScrollToTop';
import { DocumentTitleEffect } from './hooks/useDocumentTitle';

// Auth gate
import { ProtectedRoute } from './components/ProtectedRoute';

// Top-level safety net — prevents silent blank-screen failures.
import AppErrorBoundary from './components/AppErrorBoundary';

// Developer mode (RBAC impersonation, env-gated)
import DevModePanel from './components/DevModePanel';

// Phase 5 — global Cmd+K command palette (advisor only)
import CommandPalette from './components/CommandPalette';
import MemberCommandPalette from './components/MemberCommandPalette';

// Advisor pages (existing — travel operations only)
import { VeraultTravels } from './pages/VeraultTravels';
import { TripDetailPage } from './pages/TripDetailPage';
// Phase L1 — Only InvoicesPage from ManagePages is still mounted; others are dead.
import { InvoicesPage } from './pages/ManagePages';
import { SettingsPage } from './pages/SettingsPage';
import { ReferenceFormViewer } from './pages/FormViewer';

// Phase 1 — new unified Client Profile (operational command center)
import ClientProfilePage from './pages/manage/ClientProfilePage';

// Phase 2 — Payments AR/AP v2
import PaymentsPageV2 from './pages/manage/PaymentsPageV2';

// Phase L1 — ClientHome, ClientTripDetail deleted (replaced by /portal/client TripHome)

// CMS Content pages
import ContentHub from './pages/content/ContentHub';
import MediaLibrary from './pages/content/MediaLibrary';
import JournalList from './pages/content/JournalList';
import JournalEditor from './pages/content/JournalEditor';
import DestinationsCMS from './pages/content/DestinationsCMS';
import GuidesCMS from './pages/content/GuidesCMS';

// Phase L1 — ClientPortal, ClientItineraryView deleted (replaced by /portal/client TripHome + Itinerary)

// Token-based pages (invitation / one-time actions only)
import IntakeForm from './pages/IntakeForm';
import { FormViewer } from './pages/FormViewer';
import OnboardingForm from './pages/OnboardingForm';

// ✦ VÉRAULT Portal (refinement phase) — new /portal/* route tree
import { AdvisorPortalRoutes, ClientPortalRoutes } from './portal/routes';

import './App.css';

// Configure axios to always send credentials (httpOnly cookies)
import axios from 'axios';
axios.defaults.withCredentials = true;

// Wave 12.d — keep the backend pod warm while any tab is open.
import { useBackendKeepAlive } from './hooks/useBackendKeepAlive';

function App() {
  useBackendKeepAlive();
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppErrorBoundary>
          <AuthProvider>
            <ScrollToTop />
            <DocumentTitleEffect />
            <Routes>
            {/* ========================================
                PUBLIC WEBSITE (no auth required)
                ======================================== */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<RootRouter />} />
              <Route path="/about" element={<CMSLivePage slug="about" Fallback={AboutPage} />} />
              <Route path="/services" element={<CMSLivePage slug="services" Fallback={ServicesPage} />} />
              <Route path="/destinations" element={
                <CMSLivePage
                  slug="destinations"
                  Fallback={DestinationsPage}
                  slotOverrides={{ destinations_grid: <DestinationsPage /> }}
                />
              } />
              <Route path="/plan" element={
                <CMSLivePage
                  slug="plan"
                  Fallback={PlanPage}
                  slotOverrides={{ inquiry_form: <PlanPage /> }}
                />
              } />
              <Route path="/blog" element={
                <CMSLivePage
                  slug="journal"
                  Fallback={BlogPage}
                  slotOverrides={{ journal_grid: <BlogPage /> }}
                />
              } />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/:slug" element={<GuidesPage />} />
              {/* Wave 12.x · Auth separation — the unified `/login` page has
                  been killed because it surfaced advisor language to
                  members.  Anyone landing on it is sent to the studio
                  door.  Members have their own door at /suite/jackson/access. */}
              <Route path="/login" element={<Navigate to="/owner-access" replace />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/setup-account" element={<SetupAccountPage />} />
              {/* Website CMS preview — public-shell route, draft mode requires advisor cookie */}
              <Route path="/preview/:slug" element={<PreviewPage />} />
            </Route>

            {/* Wave 26.18j — Trip Landing Page (public, full-bleed, no nav chrome) */}
            <Route path="/trip/:slug" element={<LandingPage />} />

            {/* Wave 12 (revised) · Jackson Member Suite — TWO-step door:
                · /suite/jackson         → public-safe trip landing page
                · /suite/jackson/access  → branded credential login
                Authorized emails enforced server-side via 18-day
                access_expires_at on the user record. No private trip
                details surface before authentication. */}
            <Route path="/suite/jackson" element={<JacksonSuiteLanding />} />
            <Route path="/suite/jackson/access" element={<JacksonSuiteLogin />} />
            {/* Feb 2026 client-readiness pass — restored approved flow:
                /suite/:slug → access/login first (NEVER reveal personalized
                content before authentication for private trips).
                /suite/:slug/welcome → dark editorial foyer (post-login).
                /suite/:slug/access → login page (unchanged dark design). */}
            <Route path="/suite/:slug" element={<Navigate to="access" replace />} />
            <Route path="/suite/:slug/access" element={<SuiteAccessLogin />} />
            <Route path="/suite/:slug/welcome" element={<SuiteLandingPublic />} />

            {/* Wave 12.v / 12.x — Owner / Advisor door.
                /owner-access now hosts the proper advisor email+password
                login form.  The old PIN-gated quick-nav hub remains
                available at /owner-hub for Antonia's internal nav. */}
            <Route path="/owner-access" element={<OwnerAccessLogin />} />
            <Route path="/owner-hub" element={<OwnerAccessHub />} />
            <Route path="/internal-links" element={<Navigate to="/owner-hub" replace />} />

            {/* Feb 2026 — Neutral client access page. Replaces the historical
                hardcoded fallback to /suite/jackson/access so unauthenticated
                clients hitting /portal/client (or any client route with no
                slug context) never accidentally see another family's branding. */}
            <Route path="/access" element={<NeutralAccessPage />} />

            {/* ========================================
                ADVISOR PORTAL (requires advisor role)
                ======================================== */}
            <Route element={<ProtectedRoute requiredRole="advisor" />}>
              <Route path="/advisor" element={<VeraultTravels />} />
              <Route path="/advisor/trips/:tripId" element={<TripDetailPage />} />
              {/* Phase 1+4 — legacy /advisor/manage/* + /advisor/communications now redirect
                  to their /portal/advisor/* equivalents (see redirects below). Only paths
                  that have NO portal replacement stay mounted as pages: invoices, clientProfile, settings, content editors. */}
              <Route path="/advisor/manage/clients/:clientId" element={<ClientProfilePage />} />
              <Route path="/advisor/manage/invoices" element={<InvoicesPage />} />
              <Route path="/advisor/settings" element={<SettingsPage />} />
              <Route path="/advisor/settings/calendar" element={<SettingsPage />} />
              <Route path="/advisor/settings/calendar/callback" element={<SettingsPage />} />
              <Route path="/advisor/form/template/:templateId" element={<ReferenceFormViewer />} />
              {/* CMS Content Routes — full-bleed editors intentionally outside /portal */}
              <Route path="/advisor/content" element={<ContentHub />} />
              <Route path="/advisor/content/media" element={<MediaLibrary />} />
              <Route path="/advisor/content/journal" element={<JournalList />} />
              <Route path="/advisor/content/journal/:id" element={<JournalEditor />} />
              <Route path="/advisor/content/destinations" element={<DestinationsCMS />} />
              <Route path="/advisor/content/destinations/:id" element={<DestinationsCMS />} />
              <Route path="/advisor/content/guides" element={<GuidesCMS />} />
              <Route path="/advisor/content/guides/:id" element={<GuidesCMS />} />
            </Route>

            {/* ========================================
                CLIENT PORTAL — all legacy /client/* routes now redirect to /portal/client/*
                (see Navigate block below). The legacy ClientHome/TripDetail/ItineraryView
                pages remain on disk but are no longer mounted.
                ======================================== */}

            {/* ========================================
                TOKEN-BASED PAGES (invitation / one-time)
                ======================================== */}
            <Route path="/intake/:token" element={<IntakeForm />} />
            <Route path="/form/:token" element={<FormViewer />} />
            <Route path="/onboarding/:token" element={<OnboardingForm />} />

            {/* ========================================
                ✦ VÉRAULT PORTAL (refinement phase)
                New /portal/* route tree.  Lives alongside legacy /advisor and
                /client routes which remain as readonly reference until the new
                portal is fully populated.
                ======================================== */}
            <Route element={<ProtectedRoute requiredRole="advisor" />}>
              <Route path="/portal/advisor/*" element={<AdvisorPortalRoutes />} />
            </Route>
            <Route element={<ProtectedRoute requiredRole="client" />}>
              <Route path="/portal/client/*" element={<ClientPortalRoutes />} />
            </Route>
            <Route path="/portal" element={<Navigate to="/portal/advisor" replace />} />

            {/* ========================================
                LEGACY REDIRECTS
                ======================================== */}
            {/* VT Legacy → /portal redirects (Pass C+1) ====================
                Strict VT-only retirement (HoV is protected).
                Map source: /app/memory/VT_RETIREMENT_MAP.md §D
            ============================================================ */}
            <Route path="/advisor/manage" element={<Navigate to="/portal/advisor/journeys" replace />} />
            <Route path="/advisor/manage/trips" element={<Navigate to="/portal/advisor/journeys" replace />} />
            <Route path="/advisor/manage/bookings" element={<Navigate to="/portal/advisor/bookings" replace />} />
            <Route path="/advisor/manage/clients" element={<Navigate to="/portal/advisor/clients" replace />} />
            <Route path="/advisor/manage/payments" element={<Navigate to="/portal/advisor/payments" replace />} />
            <Route path="/advisor/manage/forms" element={<Navigate to="/portal/advisor/forms" replace />} />
            <Route path="/advisor/communications" element={<Navigate to="/portal/advisor/concierge" replace />} />
            <Route path="/client" element={<Navigate to="/portal/client" replace />} />
            <Route path="/client/trip/:tripId" element={<LegacyClientTripRedirect />} />
            <Route path="/client/itinerary/:tripId" element={<LegacyClientItineraryRedirect />} />
            {/* Kept mounted (no replacement yet): /advisor (VeraultTravels), /advisor/trips/:tripId (TripDetail),
                /advisor/manage/invoices (pending payments/invoices decision), /advisor/settings (legacy backstop),
                /advisor/content/* editors (intentional Studio escape) */}
            <Route path="/manage" element={<Navigate to="/advisor/manage" replace />} />
            <Route path="/manage/*" element={<Navigate to="/advisor/manage" replace />} />
            <Route path="/travels/trips/:tripId" element={<LegacyTripRedirect />} />
            <Route path="/communications-hub" element={<Navigate to="/advisor/communications" replace />} />
            <Route path="/settings" element={<Navigate to="/advisor/settings" replace />} />
            <Route path="/settings/*" element={<Navigate to="/advisor/settings" replace />} />
            <Route path="/clients" element={<Navigate to="/advisor/manage/clients" replace />} />
            <Route path="/clients/:clientId" element={<LegacyClientRedirect />} />
            {/* Legacy magic-link — redirect to the neutral access page; the
                client must use the link sent by their advisor (which carries
                the specific suite slug). Avoids leaking Jackson's branding
                to other clients. */}
            <Route path="/trip/:token" element={<Navigate to="/access" replace />} />
            <Route path="/itinerary/:publishId" element={<Navigate to="/access" replace />} />

            {/* Feb 2026 — Short-form client links.
                Lets advisors share quick URLs like /jackson, /ifearulundu
                without the /suite/ prefix. ShortSlugRouter forwards to
                /suite/:slug/access. Must be placed AFTER all specific
                routes but BEFORE the catch-all `*`. */}
            <Route path="/:slug" element={<ShortSlugRouter />} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Floating Developer Mode panel — visible only to accounts with the developer role
              when ENABLE_DEV_MODE=true.  Rendered globally so it persists across navigations. */}
          <DevModePanel />
          {/* Phase 5 — Global Cmd+K palette.  Visible only to authenticated non-client users. */}
          <CommandPalette />
          {/* Wave 26.20 Phase 1b — Member Suite palette. Client-only, same shortcut. */}
          <MemberCommandPalette />
          </AuthProvider>
        </AppErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function LegacyTripRedirect() {
  const { tripId } = useParams();
  return <Navigate to={`/advisor/trips/${tripId}`} replace />;
}

function LegacyClientRedirect() {
  const { clientId } = useParams();
  return <Navigate to={`/advisor/manage/clients/${clientId}`} replace />;
}

function LegacyClientTripRedirect() {
  const { tripId } = useParams();
  return <Navigate to={`/portal/client/trip/${tripId}`} replace />;
}

function LegacyClientItineraryRedirect() {
  const { tripId } = useParams();
  return <Navigate to={`/portal/client/trip/${tripId}/itinerary`} replace />;
}

export default App;
